import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { useNavbarShallowStore } from "../../../../store";
import { PanelLeft, SquarePen, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getInboxView } from "../../utils";
import { useInboxSearchParams } from "../../hooks";
import { InboxFilterDialog } from "./InboxFilterDialog";
import { InboxSavedViewControls } from "./InboxSavedViewControls";
import { useState } from "react";
import { Link } from "../../../../components";
import { useLocation } from "react-router-dom";
import { PAGE_HEADER_SIZE_STRING } from "../../../../constants/header-sizing";
import { useAuth } from "@tellescope/tanstack-query";
import { ChannelIcon } from "./ChannelIcon";
import { PermissionWrapper } from "../../../../permissions";
import { hasPhoneNumberConfigured } from "../../../../utils";

export const InboxThreadListHeader = () => {
  const { t } = useTranslation("inbox");
  const location = useLocation();
  const { selectedOrganization } = useAuth();
  const view = getInboxView(location);
  const [filterDialogIsOpen, setFilterDialogIsOpen] = useState(false);
  const [search, setSearch] = useState<string | undefined>("");
  useDebouncedValue(search, 300, {
    onDebounce: () => {
      setInboxQueryParams((prev) => ({
        ...prev,
        search: search || undefined,
      }));
    },
  });
  const [inboxQueryParams, setInboxQueryParams] = useInboxSearchParams();
  const { toggleCollapsed } = useNavbarShallowStore("toggleCollapsed");

  const hasPhone = !!selectedOrganization && hasPhoneNumberConfigured(selectedOrganization);

  return (
    <>
      <div className={`flex items-center justify-between pl-2 pr-4 py-2 border-b h-[${PAGE_HEADER_SIZE_STRING}]`}>
        <div className="flex items-center gap-2">
          <Button variant="ghost" iconOnly onClick={toggleCollapsed}>
            <PanelLeft className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" iconOnly data-trackingid="messaging-open-compose-dropdown-menu">
                <SquarePen className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Link to={`/messaging/${view}/chat/new`} keepSearch data-trackingid="messaging-open-compose-chat-link">
                <DropdownMenuItem>
                  <ChannelIcon channel="Chat" className="mr-2 h-4 w-4" />
                  {t("Chat")}
                </DropdownMenuItem>
              </Link>
              <Link to={`/messaging/${view}/email/new`} keepSearch data-trackingid="messaging-open-compose-email-link">
                <DropdownMenuItem>
                  <ChannelIcon channel="Email" className="mr-2 h-4 w-4" />
                  {t("Email")}
                </DropdownMenuItem>
              </Link>
              {hasPhone && (
                <Link to={`/messaging/${view}/sms/new`} keepSearch data-trackingid="messaging-open-compose-sms-link">
                  <DropdownMenuItem>
                    <ChannelIcon channel="SMS" className="mr-2 h-4 w-4" />
                    {t("SMS")}
                  </DropdownMenuItem>
                </Link>
              )}
              {selectedOrganization?.hasConnectedZendesk && selectedOrganization?.ticketThreadsEnabled && (
                <PermissionWrapper model="ticket_threads" action="create">
                  <Link
                    to={`/messaging/${view}/zendesk/new`}
                    keepSearch
                    data-trackingid="messaging-open-compose-zendesk-link"
                  >
                    <DropdownMenuItem>
                      <ChannelIcon channel="Zendesk" className="w-[12px] h-[12px] ml-0.5 mr-2.5" />
                      {t("Zendesk")}
                    </DropdownMenuItem>
                  </Link>
                </PermissionWrapper>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <InboxSavedViewControls onOpenFilterDialog={() => setFilterDialogIsOpen(true)} />
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 py-4">
        <div className="flex items-center">
          <Input
            placeholder={t("Search messages, subjects, users...")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            data-trackingid="messaging-search-input"
          />
          {!!inboxQueryParams.search && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              hoverLabel={t("Clear search")}
              onClick={() => setSearch("")}
              data-testid="messaging-search-clear"
              className="ml-2"
            >
              <X />
            </Button>
          )}
        </div>
        <InboxFilterDialog open={filterDialogIsOpen} setOpen={setFilterDialogIsOpen} />
      </div>
    </>
  );
};
